import { Skeleton, toast } from "@credit-acceptance/uikit";
import { CancelledError, useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { useEffect, useId, useRef, useState } from "react";
import { type Control, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../../components/ui/button";
import { DialogFooter } from "../../../../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../../components/ui/select";
import { Checkbox } from "../../../../../components/ui/uikit/checkbox";
import { Textarea } from "../../../../../components/ui/uikit/textarea";
import { formatSsn } from "../../../../../lib/format";
import { getErrorMessage } from "../../../../../lib/query/errors";
import { cn } from "../../../../../lib/utils";
import ForceExitWarningModal from "../../../../exit/ForceExitWarningModal";
import { useDscWorkbench } from "../../../api/useDscWorkbench";
import { mapResidenceToSavedFields } from "../api/callScriptResidence.mapping";
import type { ResidenceCallScriptSaveWarning } from "../api/callScriptResidence.savePayload";
import {
  EMPTY_RESIDENCE_VERIFIED_FIELDS,
  type ResidenceFieldKey,
  type ResidenceVerifiedFields,
} from "../api/callScriptResidence.types";
import { buildResidenceQuestionPkMap } from "../api/callScriptResidenceQuestions";
import { useCallScriptResidenceHistory } from "../api/useCallScriptResidenceHistory";
import {
  ensureCallScriptResidenceQuestionsForPerson,
  prefetchAllCallScriptResidenceQuestions,
} from "../api/useCallScriptResidenceQuestions";
import { useSaveCallScriptResidence } from "../api/useSaveCallScriptResidence";
import { CITY_OPTIONS, US_STATE_OPTIONS } from "../constants";
import { formatPhoneOrDigits } from "../fields/PhoneNumberInput";
import { useFormReadOnly } from "../readOnlyContext";
import type { SignersInput } from "../Signers.schema";
import {
  buildChangedFieldRows,
  type ChangedFieldRow,
  type ComparisonRowOfKinds,
  isRowChanged,
} from "./callScriptComparison";
import { CallScriptConfirmSaveDialog } from "./CallScriptConfirmSaveDialog";
import { CallScriptDialogShell } from "./CallScriptDialogShell";
import { CallScriptFieldControl } from "./CallScriptFieldControl";
import { CallScriptHistoryPanel } from "./CallScriptHistoryPanel";
import { CallScriptSaveBanners } from "./CallScriptSaveBanners";
import { useCallScriptOpenOrchestration } from "./useCallScriptOpenOrchestration";

type PersonPath = "driver" | "secondSigner";

export type { ResidenceVerifiedFields } from "../api/callScriptResidence.types";
export { EMPTY_RESIDENCE_VERIFIED_FIELDS } from "../api/callScriptResidence.types";

/** Any single field's value, for the generic row/control helpers below. */
type ResidenceFieldValue = ResidenceVerifiedFields[ResidenceFieldKey];

// ---------------------------------------------------------------------------
// Render flags
//
// CAPS2's `verificationXxxRendered` booleans (model bean lines 428-469). Every
// flag defaults to `true`; `showAndHideTheFields()` flips one to `false` when
// its question text comes back in getExpiredVerificationQuestionList(...) for
// the deal's submitted date — i.e. each row is shown UNLESS that question has
// expired. Modelled here as a prop rather than derived state: this component
// only lays the fields out, so whoever wires the expiry lookup owns computing
// these.
//
// Five rows have no flag at all and always render (Gross Trade-In Amount,
// Trade-In Payoff Amount, Cash Down Paid on Delivery, Amount Due After
// Delivery, When is the Final Amount Due), as do both address section headers.
// ---------------------------------------------------------------------------

/** One flag per expirable question; `true` (the default) renders the row. */
export type ResidenceCallScriptRenderFlags = Record<
  Exclude<
    ResidenceFieldKey,
    | "grossTradeInAmount"
    | "tradeInPayoffAmount"
    | "cashDownPaidOnDelivery"
    | "amountDueAfterDelivery"
    | "whenIsFinalAmountDue"
  >,
  boolean
>;

const FLAGGED_FIELD_KEYS: ReadonlyArray<keyof ResidenceCallScriptRenderFlags> = [
  "phoneNumberDialed",
  "wasOriginalNumberBad",
  "dateOfBirth",
  "primaryNumber",
  "secondaryNumber",
  "consentToSignElectronically",
  "signedCopyProvided",
  "physicalStreetNumber",
  "physicalStreetName",
  "physicalApartmentNumber",
  "physicalZip",
  "physicalCity",
  "physicalState",
  "mailingStreetNumber",
  "mailingStreetName",
  "mailingApartmentNumber",
  "mailingZip",
  "mailingCity",
  "mailingState",
  "whoIsPrimaryDriver",
  "tradeIn",
  "totalCashAndTradeIn",
  "anyAmountStillOwed",
  "whenIsRemainingDue",
];

/** Every expirable question shown — the state CAPS2 starts from before the expiry pass runs. */
export const DEFAULT_RESIDENCE_CALL_SCRIPT_RENDER_FLAGS: ResidenceCallScriptRenderFlags = Object.fromEntries(
  FLAGGED_FIELD_KEYS.map((key) => [key, true])
) as ResidenceCallScriptRenderFlags;

/** One row of the Vehicle Accessories table (CAPS2 `customerVerificationVehicleAddonWOList`). */
export interface ResidenceCallScriptAccessory {
  id: string;
  description: string;
  price?: number;
}

// ---------------------------------------------------------------------------
// Row definitions
// ---------------------------------------------------------------------------

/** The control kinds this script uses — employment's `checkbox`/`number` have no residence rows. */
type ResidenceRowKind = "text" | "phone" | "currency" | "date" | "yesNo" | "yesNoBlank" | "select";

type FieldRow = ComparisonRowOfKinds<ResidenceFieldKey, ResidenceRowKind> & {
  type: "field";
  /** Absent for the five unconditional down-payment amounts. */
  flag?: keyof ResidenceCallScriptRenderFlags;
};

/** A full-width section heading (always rendered — CAPS2 puts no flag on either address header). */
interface SectionRow {
  type: "section";
  key: string;
  label: string;
  /** Renders the "same as physical" checkbox beside the heading (Mailing Address only). */
  withSameAsPhysical?: boolean;
}

type Row = FieldRow | SectionRow;

interface BuildRowsOptions {
  t: TFunction;
  /** CAPS2 `verificationPANoCity` — the ZIP resolved no city list, so City is free text instead of a dropdown. */
  physicalNoCity: boolean;
  /** CAPS2 `verificationMANoCity`. */
  mailingNoCity: boolean;
}

function buildRows({ t, physicalNoCity, mailingNoCity }: BuildRowsOptions): Row[] {
  return [
    // --- Contact block ---
    {
      type: "field",
      key: "phoneNumberDialed",
      flag: "phoneNumberDialed",
      label: t("review.customer.callScriptModal.fields.phoneNumberDialed"),
      kind: "phone",
      placeholder: "000-000-0000",
    },
    {
      type: "field",
      key: "wasOriginalNumberBad",
      flag: "wasOriginalNumberBad",
      label: t("review.customer.callScriptModal.fields.wasOriginalNumberBad"),
      kind: "yesNo",
    },
    {
      type: "field",
      key: "dateOfBirth",
      flag: "dateOfBirth",
      label: t("review.customer.callScriptModal.fields.dateOfBirth"),
      kind: "date",
    },
    {
      type: "field",
      key: "primaryNumber",
      flag: "primaryNumber",
      label: t("review.customer.callScriptModal.fields.primaryNumber"),
      kind: "phone",
      placeholder: "000-000-0000",
    },
    {
      type: "field",
      key: "secondaryNumber",
      flag: "secondaryNumber",
      label: t("review.customer.callScriptModal.fields.secondaryNumber"),
      kind: "phone",
      placeholder: "000-000-0000",
    },
    // Second half of this row's condition (the VLA's isEcontractApp === "Y")
    // is applied by the `isEcontractApp` prop, not by a render flag.
    {
      type: "field",
      key: "consentToSignElectronically",
      flag: "consentToSignElectronically",
      label: t("review.customer.callScriptModal.fields.consentToSignElectronically"),
      kind: "yesNoBlank",
    },
    {
      type: "field",
      key: "signedCopyProvided",
      flag: "signedCopyProvided",
      label: t("review.customer.callScriptModal.fields.signedCopyProvided"),
      kind: "yesNoBlank",
    },

    // --- Physical address ---
    { type: "section", key: "physicalAddress", label: t("review.customer.callScriptModal.sections.physicalAddress") },
    {
      type: "field",
      key: "physicalStreetNumber",
      flag: "physicalStreetNumber",
      label: t("review.customer.callScriptModal.fields.physicalStreetNumber"),
      kind: "text",
    },
    {
      type: "field",
      key: "physicalStreetName",
      flag: "physicalStreetName",
      label: t("review.customer.callScriptModal.fields.physicalStreetName"),
      kind: "text",
    },
    {
      type: "field",
      key: "physicalApartmentNumber",
      flag: "physicalApartmentNumber",
      label: t("review.customer.callScriptModal.fields.physicalApartmentNumber"),
      kind: "text",
    },
    {
      type: "field",
      key: "physicalZip",
      flag: "physicalZip",
      label: t("review.customer.callScriptModal.fields.physicalZip"),
      kind: "text",
      placeholder: "00000",
    },
    {
      type: "field",
      key: "physicalCity",
      flag: "physicalCity",
      label: t("review.customer.callScriptModal.fields.physicalCity"),
      kind: "select",
      options: CITY_OPTIONS,
      allowFreeText: physicalNoCity,
    },
    {
      type: "field",
      key: "physicalState",
      flag: "physicalState",
      label: t("review.customer.callScriptModal.fields.physicalState"),
      kind: "select",
      options: US_STATE_OPTIONS,
    },

    // --- Mailing address ---
    {
      type: "section",
      key: "mailingAddress",
      label: t("review.customer.callScriptModal.sections.mailingAddress"),
      withSameAsPhysical: true,
    },
    {
      type: "field",
      key: "mailingStreetNumber",
      flag: "mailingStreetNumber",
      label: t("review.customer.callScriptModal.fields.mailingStreetNumber"),
      kind: "text",
    },
    {
      type: "field",
      key: "mailingStreetName",
      flag: "mailingStreetName",
      label: t("review.customer.callScriptModal.fields.mailingStreetName"),
      kind: "text",
    },
    {
      type: "field",
      key: "mailingApartmentNumber",
      flag: "mailingApartmentNumber",
      label: t("review.customer.callScriptModal.fields.mailingApartmentNumber"),
      kind: "text",
    },
    {
      type: "field",
      key: "mailingZip",
      flag: "mailingZip",
      label: t("review.customer.callScriptModal.fields.mailingZip"),
      kind: "text",
      placeholder: "00000",
    },
    // NOTE: CAPS2 gates this row's SAVED cell on verificationMAStateRendered
    // instead of verificationMACityRendered (known defect #1 — the saved
    // Mailing City disappears whenever Mailing State expires, leaving the
    // input with nothing to compare against). Not reproduced here: the whole
    // row, both columns, hangs off the City flag like the Physical block does.
    {
      type: "field",
      key: "mailingCity",
      flag: "mailingCity",
      label: t("review.customer.callScriptModal.fields.mailingCity"),
      kind: "select",
      options: CITY_OPTIONS,
      allowFreeText: mailingNoCity,
    },
    {
      type: "field",
      key: "mailingState",
      flag: "mailingState",
      label: t("review.customer.callScriptModal.fields.mailingState"),
      kind: "select",
      options: US_STATE_OPTIONS,
    },

    // --- Deal / down payment block ---
    {
      type: "field",
      key: "whoIsPrimaryDriver",
      flag: "whoIsPrimaryDriver",
      label: t("review.customer.callScriptModal.fields.whoIsPrimaryDriver"),
      kind: "text",
    },
    {
      type: "field",
      key: "tradeIn",
      flag: "tradeIn",
      label: t("review.customer.callScriptModal.fields.tradeIn"),
      kind: "yesNoBlank",
    },
    {
      type: "field",
      key: "totalCashAndTradeIn",
      flag: "totalCashAndTradeIn",
      label: t("review.customer.callScriptModal.fields.totalCashAndTradeIn"),
      kind: "currency",
    },
    {
      type: "field",
      key: "anyAmountStillOwed",
      flag: "anyAmountStillOwed",
      label: t("review.customer.callScriptModal.fields.anyAmountStillOwed"),
      kind: "yesNoBlank",
    },
    {
      type: "field",
      key: "whenIsRemainingDue",
      flag: "whenIsRemainingDue",
      label: t("review.customer.callScriptModal.fields.whenIsRemainingDue"),
      kind: "date",
    },
    // The five below carry no render flag in CAPS2 — always shown, populated
    // by buildScreenForDeferredDown(dealsInWork, deferredDownPaymentDetail).
    {
      type: "field",
      key: "grossTradeInAmount",
      label: t("review.customer.callScriptModal.fields.grossTradeInAmount"),
      kind: "currency",
    },
    {
      type: "field",
      key: "tradeInPayoffAmount",
      label: t("review.customer.callScriptModal.fields.tradeInPayoffAmount"),
      kind: "currency",
    },
    {
      type: "field",
      key: "cashDownPaidOnDelivery",
      label: t("review.customer.callScriptModal.fields.cashDownPaidOnDelivery"),
      kind: "currency",
    },
    {
      type: "field",
      key: "amountDueAfterDelivery",
      label: t("review.customer.callScriptModal.fields.amountDueAfterDelivery"),
      kind: "currency",
    },
    {
      type: "field",
      key: "whenIsFinalAmountDue",
      label: t("review.customer.callScriptModal.fields.whenIsFinalAmountDue"),
      kind: "date",
    },
  ];
}

/** The six mailing fields, paired with the physical field each mirrors while "same as physical" is checked. */
const MAILING_MIRROR_PAIRS: ReadonlyArray<[keyof ResidenceVerifiedFields, keyof ResidenceVerifiedFields]> = [
  ["mailingStreetNumber", "physicalStreetNumber"],
  ["mailingStreetName", "physicalStreetName"],
  ["mailingApartmentNumber", "physicalApartmentNumber"],
  ["mailingZip", "physicalZip"],
  ["mailingCity", "physicalCity"],
  ["mailingState", "physicalState"],
];

const MAILING_FIELD_KEYS = new Set<ResidenceFieldKey>(MAILING_MIRROR_PAIRS.map(([mailingKey]) => mailingKey));

interface CallScriptModalResidenceProperties {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Deal / approval identifier — path param for both Call Script calls, and displayed in the side panel. */
  approvalId?: string;
  /** Applicant identifier — path param for both Call Script calls. */
  applicantId?: string;
  /** Which signer this call is for — drives the title and the side panel's name/SSN. */
  personPath: PersonPath;
  /** RHF control — reads the person's name/SSN/primary phone for the side panel. */
  control: Control<SignersInput>;
  /**
   * CAPS2 `verificationXxxRendered`: a row is hidden when its question has
   * expired relative to the deal's submitted date. Anything omitted defaults
   * to shown.
   */
  renderFlags?: Partial<ResidenceCallScriptRenderFlags>;
  /** VLA `isEcontractApp === "Y"` — second half of the Consent to sign electronically condition. */
  isEcontractApp?: boolean;
  /** CAPS2 `verificationPANoCity` — render Physical City as free text rather than a dropdown. */
  physicalNoCity?: boolean;
  /** CAPS2 `verificationMANoCity`. */
  mailingNoCity?: boolean;
  /**
   * Vehicle accessories to ask about. CAPS2 keeps this block in the component
   * tree and only toggles `visible`, so an empty list hides it rather than
   * unmounting it.
   */
  accessories?: readonly ResidenceCallScriptAccessory[];
}

// ---------------------------------------------------------------------------
// CallScriptModalResidence
//
// Draggable, dismissable "Customer Verification" call script — the residence
// sibling of CallScriptModal.tsx (employment). In CAPS2 these are two entirely
// separate screens with no shared code: this one is
// step6_customer_verification.xhtml, driven by
// Step6ApplicantResidenceVerificationBackingBean, opened from the driver /
// second-signer phone+email panel's "Call Script" button.
//
// Save vs. close, deliberately split (a verification session can involve
// several saves before the analyst is done): a successful save refreshes this
// applicant's history and clears "Verified Information" back to blank for the
// next call, while closing the modal has nothing special to do as a result.
//
// SCOPE: Save POSTs .../residence-verification/call-script
// (useSaveCallScriptResidence) and the History panel reads
// .../residence-verification/call-script/history
// (useCallScriptResidenceHistory). The verification-question catalog
// (useCallScriptResidenceQuestions) is prefetched on open and awaited at Save
// to resolve each answer's verificationQuestionPk. "Saved Information" (Contact + Physical/Mailing Address) is read live from the
// dashboard, same as the employment modal — see callScriptResidence.mapping.
// The Deal/down-payment block has no BFF backing anywhere in this app, so
// those rows stay at their empty defaults regardless. Deliberately NOT
// mirrored from the employment modal: the ZIP -> City/State autofill.
// ---------------------------------------------------------------------------
export function CallScriptModalResidence({
  open,
  onOpenChange,
  approvalId,
  applicantId,
  personPath,
  control,
  renderFlags,
  isEcontractApp = false,
  physicalNoCity = false,
  mailingNoCity = false,
  accessories = [],
}: CallScriptModalResidenceProperties) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const readOnly = useFormReadOnly();
  const uid = useId();
  const hasIds = Boolean(approvalId && applicantId);

  const dashboard = useDscWorkbench(approvalId ?? "");
  const residence = dashboard.data?.[personPath]?.residence;
  // The primary key of the applicant's primary (current/physical) residence
  // address, for the save contract's applicantAddressPk. Falls back to
  // mailing's addressId whenever current itself is absent OR present but not
  // yet carrying one — v2 exposes no `previous[]`, so mailing is the only
  // other address slot that could carry one.
  const applicantAddressPk = residence?.current?.addressId ?? residence?.mailing?.addressId;

  const { isPreparingModal, prepareError, forceExitWarningOpen, setForceExitWarningOpen, retry } =
    useCallScriptOpenOrchestration({
      open,
      approvalId,
      applicantId,
      readOnly,
      onOpenChange,
      prepareSaveFailedMessage: t("review.customer.callScriptModal.prepareSaveFailed"),
    });

  const [verified, setVerified] = useState<ResidenceVerifiedFields>(EMPTY_RESIDENCE_VERIFIED_FIELDS);
  const [note, setNote] = useState("");
  const [doNotContactPrimary, setDoNotContactPrimary] = useState(false);
  // Screen-only mirror (the legacy popup has the same checkbox beside the
  // Mailing Address header): while checked, the six mailing rows copy physical
  // and their inputs are disabled.
  const [mailingSameAsPhysical, setMailingSameAsPhysical] = useState(false);
  const [accessoryAnswers, setAccessoryAnswers] = useState<Record<string, string>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [changedFieldRows, setChangedFieldRows] = useState<Array<ChangedFieldRow<ResidenceFieldKey>>>([]);
  // A successful save can still carry non-fatal warnings — grouped by `code`
  // for display, cleared on open and replaced (not appended) by each save.
  const [saveWarnings, setSaveWarnings] = useState<ResidenceCallScriptSaveWarning[]>([]);
  // Save first awaits the question catalog (see submitSave); a failure there
  // never reaches the mutation, so it's tracked separately from saveCallScript.error.
  const [isResolvingQuestions, setIsResolvingQuestions] = useState(false);
  const [questionsError, setQuestionsError] = useState<string>();

  const history = useCallScriptResidenceHistory(approvalId ?? "", applicantId ?? "", { enabled: open && hasIds });
  const saveCallScript = useSaveCallScriptResidence();
  useEffect(() => {
    if (open && approvalId && applicantId) {
      prefetchAllCallScriptResidenceQuestions(queryClient, approvalId, applicantId);
    }
  }, [open, approvalId, applicantId, queryClient]);

  const firstName = useWatch({ control, name: `${personPath}.customerInfo.firstName` });
  const lastName = useWatch({ control, name: `${personPath}.customerInfo.lastName` });
  const ssn = useWatch({ control, name: `${personPath}.customerInfo.ssnOrItin` });
  const primaryPhone = useWatch({ control, name: `${personPath}.customerInfo.phones.0.number` });
  const personName = [firstName, lastName].filter(Boolean).join(" ");
  const personLabel = t(personPath === "driver" ? "signers.driver" : "signers.secondSigner");
  const noSavedValue = t("review.customer.callScriptModal.noSavedValue");

  const entries = history.data?.entries ?? [];
  const savedFieldsBaseline = mapResidenceToSavedFields(residence);
  // Phone Number Dialed has no saved counterpart of its own — the last call's
  // dialed number is what "Saved Information" shows, same as the employment
  // modal reads it off the newest history entry.
  const savedFields: ResidenceVerifiedFields = {
    ...savedFieldsBaseline,
    phoneNumberDialed: entries[0]?.phoneNumberDialed ?? savedFieldsBaseline.phoneNumberDialed,
  };

  /**
   * Clears "Verified Information" back to blank. Shared by the on-open reset
   * and the post-save reset.
   */
  function resetVerifiedInformation() {
    setVerified(EMPTY_RESIDENCE_VERIFIED_FIELDS);
    setMailingSameAsPhysical(false);
    setAccessoryAnswers({});
  }

  // "Verified Information" is what the analyst captures live on the call, so
  // every open starts from blank.
  useEffect(() => {
    if (open) {
      resetVerifiedInformation();
      setNote("");
      setDoNotContactPrimary(false);
      setConfirmOpen(false);
      setChangedFieldRows([]);
      setSaveWarnings([]);
      setQuestionsError(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resetVerifiedInformation is stable per render; only `open` should retrigger this
  }, [open]);

  // TanStack Query v5 dropped per-query `onError`, so query failures are
  // toasted from an effect (mirrors CallScriptModal).
  // eslint-disable-next-line unicorn/no-useless-undefined -- useRef's no-init overload still requires the arg here
  const toastedHistoryErrorReference = useRef<unknown>(undefined);
  useEffect(() => {
    if (history.isError && history.error !== toastedHistoryErrorReference.current) {
      toastedHistoryErrorReference.current = history.error;
      toast.error(t("review.customer.callScriptModal.history.error", { message: getErrorMessage(history.error) }));
    } else if (!history.isError) {
      toastedHistoryErrorReference.current = undefined;
    }
  }, [history.isError, history.error, t]);

  const flags: ResidenceCallScriptRenderFlags = { ...DEFAULT_RESIDENCE_CALL_SCRIPT_RENDER_FLAGS, ...renderFlags };
  const rows = buildRows({ t, physicalNoCity, mailingNoCity });
  const isSaving = saveCallScript.isPending || isResolvingQuestions;
  const canSave = hasIds && !readOnly && !isSaving && !isPreparingModal && !prepareError;
  const saveErrorMessage =
    questionsError ?? (saveCallScript.isError ? getErrorMessage(saveCallScript.error) : undefined);

  function updateVerified(key: ResidenceFieldKey, value: ResidenceFieldValue) {
    setVerified((previous) => {
      const next = { ...previous, [key]: value };
      // Keep mailing in lockstep while the mirror is on — the mailing inputs
      // are disabled in that state, so this is their only writer.
      if (mailingSameAsPhysical) {
        for (const [mailingKey, physicalKey] of MAILING_MIRROR_PAIRS) {
          if (physicalKey === key) {
            next[mailingKey] = value as never;
          }
        }
      }
      return next;
    });
  }

  function handleMailingSameAsPhysicalChange(checked: boolean) {
    setMailingSameAsPhysical(checked);
    setVerified((previous) => {
      const next = { ...previous };
      for (const [mailingKey, physicalKey] of MAILING_MIRROR_PAIRS) {
        // Unchecking clears mailing rather than leaving the mirrored values
        // behind, so the analyst always re-enters from blank (matches the
        // Residence tab's useMirrorAddress).
        next[mailingKey] = (checked ? previous[physicalKey] : "") as never;
      }
      return next;
    });
  }

  /**
   * Whether a row survives CAPS2's render conditions.
   * @param {FieldRow} row - The field-row definition.
   * @returns {boolean} True when the row should be rendered.
   */
  function isRowVisible(row: FieldRow): boolean {
    if (row.key === "consentToSignElectronically") {
      // Two-term condition: the render flag AND showConSigElectronically
      // (the VLA's isEcontractApp === "Y").
      return flags.consentToSignElectronically && isEcontractApp;
    }
    return row.flag === undefined || flags[row.flag];
  }

  /**
   * Persists the call, then clears "Verified Information" for the next one.
   * The employment modal re-checks force-exit again right here, at the moment
   * Save is confirmed (its cached value could be stale by then); this modal
   * only re-checks once, on open (see useCallScriptOpenOrchestration) — a
   * second check at save-time is deliberately out of scope for now.
   *
   * The question catalog is awaited (not read from cache) so a Save that
   * races the modal-open prefetch still resolves every answer's
   * verificationQuestionPk instead of posting an empty verificationAnswers[]
   * — see ensureCallScriptResidenceQuestionsForPerson.
   */
  async function submitSave() {
    if (!approvalId || !applicantId) {
      return;
    }
    // Cleared here (not just on open) so a warning from an earlier successful
    // save doesn't linger alongside a failure from this attempt.
    setSaveWarnings([]);
    setQuestionsError(undefined);
    setIsResolvingQuestions(true);
    let questions;
    try {
      questions = await ensureCallScriptResidenceQuestionsForPerson(queryClient, approvalId, applicantId, personPath);
    } catch (error) {
      if (!(error instanceof CancelledError)) {
        setQuestionsError(getErrorMessage(error));
      }
      return;
    } finally {
      setIsResolvingQuestions(false);
    }
    setConfirmOpen(false);
    saveCallScript.mutate(
      {
        approvalId,
        applicantId,
        personPath,
        applicantAddressPk,
        note: note.trim(),
        doNotContactPrimary,
        verifiedFields: verified,
        savedFields,
        questionPkMap: buildResidenceQuestionPkMap(questions),
        previousVerificationOutcome: entries[0]?.verificationOutcome,
      },
      {
        onSuccess: (result) => {
          // `undefined` means the mutationFn no-op'd on a force-exit — nothing
          // was saved, so don't wipe what the analyst just captured.
          if (!result) {
            return;
          }
          setNote("");
          resetVerifiedInformation();
          setSaveWarnings(result.warnings ?? []);
        },
      }
    );
  }

  /**
   * Save click: opens the "CAPS WARNING" confirmation listing only the changed
   * fields, or saves straight through when nothing actually changed.
   */
  function handleSaveClick() {
    if (!canSave) {
      return;
    }
    const changed = buildChangedFieldRows(
      rows.filter((row) => row.type === "field"),
      verified,
      savedFields,
      {
        noSavedValue,
        isVisible: (row) => isRowVisible(row as FieldRow),
      }
    );
    if (changed.length === 0) {
      void submitSave();
      return;
    }
    setChangedFieldRows(changed);
    setConfirmOpen(true);
  }

  const historyEntryHeader = t("review.customer.callScriptModal.history.entryHeader", {
    personLabel: personLabel.toUpperCase(),
  });

  return (
    <>
      <CallScriptDialogShell
        open={open}
        onOpenChange={onOpenChange}
        title={t("review.customer.callScriptModal.title", { personLabel })}
        description={t("review.customer.callScriptModal.description")}
        closeLabel={t("review.customer.callScriptModal.close")}
        headerTestId="residence-call-script-header"
      >
        {isPreparingModal && (
          <div
            className="gap-(--spacing-3) px-(--spacing-6) py-(--spacing-8) bg-card flex flex-col"
            role="status"
            aria-label={t("review.customer.callScriptModal.preparing")}
          >
            <Skeleton loading active paragraph={{ rows: 6 }} />
          </div>
        )}

        {!isPreparingModal && prepareError && (
          <div className="gap-(--spacing-4) px-(--spacing-6) py-(--spacing-8) bg-card flex flex-col">
            <p
              role="alert"
              className="text-destructive text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height)"
            >
              {t("review.customer.callScriptModal.prepareErrorPrefix", { message: prepareError })}
            </p>
            <div className="gap-(--spacing-2) flex">
              <Button type="button" size="xs" onClick={retry}>
                {t("review.customer.callScriptModal.retry")}
              </Button>
              <Button type="button" variant="outline" size="xs" onClick={() => onOpenChange(false)}>
                {t("review.customer.callScriptModal.exit")}
              </Button>
            </div>
          </div>
        )}

        {!isPreparingModal && !prepareError && (
          <div className="flex flex-col">
            <div className="px-(--spacing-4) bg-card">
              <CallScriptSaveBanners
                error={
                  saveErrorMessage
                    ? t("review.customer.callScriptModal.saveError", { message: saveErrorMessage })
                    : undefined
                }
                warnings={saveWarnings}
                warningHeading={() => t("review.customer.callScriptModal.warnings.generic")}
              />
              <div className="gap-(--spacing-2) py-(--spacing-2) grid h-[75vh] grid-cols-1 lg:grid-cols-[2fr_1fr]">
                {/* Verified vs Saved comparison */}
                <div className="text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) grid min-h-0 grid-cols-1 overflow-y-auto sm:grid-cols-[auto_1fr_1fr]">
                  <div />
                  <div className="text-primary text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-semibold)">
                    {t("review.customer.callScriptModal.verifiedInformation")}
                  </div>
                  <div className="text-primary text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-semibold)">
                    {t("review.customer.callScriptModal.savedInformation")}
                  </div>

                  {rows.map((row) => {
                    if (row.type === "section") {
                      return (
                        <div key={row.key} className="contents">
                          <span className="text-primary pt-(--spacing-2) text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-semibold) self-center pr-1.5 text-right">
                            {row.label}
                          </span>
                          {row.withSameAsPhysical ? (
                            <div className="pt-(--spacing-2) flex items-center gap-1.5 self-center">
                              <Checkbox
                                id={`${uid}-mailing-same-as-physical`}
                                checked={mailingSameAsPhysical}
                                onCheckedChange={(checked) => handleMailingSameAsPhysicalChange(checked === true)}
                                disabled={readOnly}
                              />
                              <label
                                htmlFor={`${uid}-mailing-same-as-physical`}
                                className="text-muted-foreground text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height)"
                              >
                                {t("review.customer.callScriptModal.mailingSameAsPhysical")}
                              </label>
                            </div>
                          ) : (
                            <div />
                          )}
                          <div />
                        </div>
                      );
                    }
                    if (!isRowVisible(row)) {
                      return;
                    }
                    const id = `${uid}-${row.key}`;
                    const mirrored = mailingSameAsPhysical && MAILING_FIELD_KEYS.has(row.key);
                    return (
                      <div key={row.key} className="contents [&>*]:py-0">
                        {/* Not a <label htmlFor>: clicking it must not focus/activate the
                          Verified input — CallScriptFieldControl gets its name via aria-label. */}
                        <span className="text-primary text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-medium) self-center pr-1.5 text-right">
                          {row.label}
                        </span>
                        <div
                          className={cn(
                            "max-w-[90%] self-center",
                            isRowChanged(row, verified[row.key], savedFields[row.key]) && "rounded-md bg-amber-100"
                          )}
                          data-testid={`${row.key}-verified-cell`}
                        >
                          <CallScriptFieldControl
                            row={row}
                            id={id}
                            value={verified[row.key]}
                            onChange={(value) => updateVerified(row.key, value)}
                            disabled={readOnly || mirrored}
                            ariaLabel={row.label}
                          />
                        </div>
                        <div className="max-w-[90%] self-center">
                          <CallScriptFieldControl
                            row={row}
                            id={`${id}-saved`}
                            value={savedFields[row.key]}
                            onChange={() => {}}
                            disabled
                            ariaLabel={t("review.customer.callScriptModal.savedFieldLabel", { field: row.label })}
                          />
                        </div>
                      </div>
                    );
                  })}

                  {/* Vehicle accessories — CAPS2 toggles `visible`, not `rendered`,
                    so the block stays mounted and is only hidden when the deal
                    has no addons. */}
                  <div className="mt-(--spacing-3) sm:col-span-3" hidden={accessories.length === 0}>
                    <h3 className="text-primary mb-(--spacing-1) text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-semibold)">
                      {t("review.customer.callScriptModal.accessories.question")}
                    </h3>
                    <div className="gap-x-(--spacing-3) gap-y-(--spacing-1) grid grid-cols-[1fr_auto_auto]">
                      <span className="text-muted-foreground text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-semibold) uppercase">
                        {t("review.customer.callScriptModal.accessories.accessory")}
                      </span>
                      <span className="text-muted-foreground text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-semibold) uppercase">
                        {t("review.customer.callScriptModal.accessories.price")}
                      </span>
                      <span className="text-muted-foreground text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-semibold) uppercase">
                        {t("review.customer.callScriptModal.accessories.purchased")}
                      </span>
                      {accessories.map((accessory) => (
                        <div key={accessory.id} className="contents">
                          <span className="self-center">{accessory.description}</span>
                          <span className="self-center text-right tabular-nums">
                            {accessory.price === undefined
                              ? noSavedValue
                              : `$${accessory.price.toLocaleString("en-US", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}`}
                          </span>
                          <Select
                            value={accessoryAnswers[accessory.id] ?? ""}
                            onValueChange={(next) =>
                              setAccessoryAnswers((previous) => ({ ...previous, [accessory.id]: next }))
                            }
                            disabled={readOnly}
                          >
                            <SelectTrigger
                              aria-label={t("review.customer.callScriptModal.accessories.purchased")}
                              className="text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) h-5 w-16"
                            >
                              <SelectValue placeholder="" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Y">Y</SelectItem>
                              <SelectItem value="N">N</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Side panel: Approval / person / do-not-contact / Notes / History */}
                <div className="flex min-h-0 flex-col gap-2">
                  <div className="text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) leading-tight">
                    <p>
                      <span className="text-primary font-(--font-weight-semibold)">
                        {t("review.customer.callScriptModal.approvalNumber")}:
                      </span>{" "}
                      {approvalId || noSavedValue}
                    </p>
                    <p>
                      <span className="text-primary font-(--font-weight-semibold)">{personLabel}:</span>{" "}
                      {personName || noSavedValue}
                      {ssn && ` (${formatSsn(ssn)})`}
                    </p>
                    <p>
                      <span className="text-primary font-(--font-weight-semibold)">
                        {t("review.customer.callScriptModal.phoneNumber")}:
                      </span>{" "}
                      {primaryPhone ? formatPhoneOrDigits(String(primaryPhone)) : noSavedValue}
                    </p>
                  </div>

                  <div className="flex items-start gap-1.5">
                    <Checkbox
                      id={`${uid}-do-not-contact-primary`}
                      checked={doNotContactPrimary}
                      onCheckedChange={(checked) => setDoNotContactPrimary(checked === true)}
                      disabled={readOnly}
                      className="mt-0.5"
                    />
                    <label
                      htmlFor={`${uid}-do-not-contact-primary`}
                      className="text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) leading-tight"
                    >
                      {t("review.customer.callScriptModal.doNotContactPrimary", { personLabel })}
                    </label>
                  </div>

                  <div>
                    <label
                      htmlFor={`${uid}-residence-note`}
                      className="text-primary mb-(--spacing-1) text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-semibold) block"
                    >
                      {t("review.customer.callScriptModal.notes.label")}
                    </label>
                    <Textarea
                      id={`${uid}-residence-note`}
                      value={note}
                      onChange={(event) => setNote(event.target.value)}
                      placeholder={t("review.customer.callScriptModal.notes.placeholder")}
                      disabled={readOnly}
                      className="min-h-16 resize-none"
                    />
                  </div>

                  <CallScriptHistoryPanel
                    entries={entries}
                    isLoading={history.isLoading}
                    title={t("review.customer.callScriptModal.history.title")}
                    loadingLabel={t("review.customer.callScriptModal.history.loading")}
                    emptyLabel={t("review.customer.callScriptModal.history.empty")}
                    entryHeader={historyEntryHeader}
                    renderAnswerValue={(answer) => answer.answerEntered}
                  />
                </div>
              </div>

              <div className="text-muted-foreground space-y-(--spacing-2) pb-(--spacing-3) text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height)">
                <p>{t("review.customer.callScriptModal.footerNotes.accountNumber")}</p>
                <div>
                  <p>{t("review.customer.callScriptModal.footerNotes.downPaymentIntro")}</p>
                  <ul className="list-inside list-disc">
                    <li>{t("review.customer.callScriptModal.footerNotes.downPaymentKnown")}</li>
                    <li>{t("review.customer.callScriptModal.footerNotes.downPaymentUnsure")}</li>
                    <li>{t("review.customer.callScriptModal.footerNotes.downPaymentNone")}</li>
                  </ul>
                </div>
              </div>
            </div>

            <DialogFooter className="bg-muted px-(--spacing-4) py-(--spacing-2) border-t sm:flex-row">
              <Button type="button" size="xs" disabled={!canSave} onClick={handleSaveClick}>
                {t("review.customer.callScriptModal.save")}
              </Button>
              <Button type="button" variant="outline" size="xs" disabled={isSaving} onClick={() => onOpenChange(false)}>
                {t("review.customer.callScriptModal.exit")}
              </Button>
            </DialogFooter>
          </div>
        )}
      </CallScriptDialogShell>

      <CallScriptConfirmSaveDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        rows={changedFieldRows}
        onConfirm={() => void submitSave()}
        disabled={isSaving}
        error={questionsError}
        labels={{
          title: t("review.customer.callScriptModal.confirmSave.title"),
          message: t("review.customer.callScriptModal.confirmSave.message"),
          question: t("review.customer.callScriptModal.confirmSave.question"),
          verifiedInformation: t("review.customer.callScriptModal.verifiedInformation"),
          savedInformation: t("review.customer.callScriptModal.savedInformation"),
          confirm: t("review.customer.callScriptModal.confirmSave.ok"),
          cancel: t("review.customer.callScriptModal.confirmSave.cancel"),
        }}
      />

      <ForceExitWarningModal
        open={forceExitWarningOpen}
        onOpenChange={setForceExitWarningOpen}
        onConfirm={() => setForceExitWarningOpen(false)}
      />
    </>
  );
}
